// src/contexts/GeminiContext.tsx
// Migrated to Supabase

import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../supabase/config';

/**
 * The conflict target for the pit.gemini_config upsert.
 *
 * Postgres requires ON CONFLICT to name a unique index EXACTLY. This upsert
 * named `user_id`, where the only unique index was the primary key on `id`, so
 * every save raised
 *   42P10 there is no unique or exclusion constraint matching the ON CONFLICT specification
 * (issue #155). The matching index is added by
 * supabase/migrations/20260828000000_pit_gemini_config_unique_tenant_user.sql.
 *
 * The grain is one config per user per tenant, so the target is
 * (tenant_id, user_id), not a globally-unique user_id. `tenant_id` is not sent
 * in the payload — the column defaults to `pit.current_tenant()` — but it still
 * belongs in the conflict target, because conflict detection runs on the row
 * after defaults are applied.
 *
 * Same trap as #154, #172 and #180. Check pg_indexes before writing an
 * onConflict; never infer it from the columns you are writing.
 */
const GEMINI_CONFIG_CONFLICT_TARGET = 'tenant_id,user_id';

// Types
export interface GeminiConfig {
  // API Configuration
  /**
   * Write-only from the browser's point of view: the SPA may hold a key the
   * user just typed, but pit.gemini_config.api_key is never read back or
   * written by a client role (issue #257). Use `apiKeyConfigured` to show
   * whether one is stored.
   */
  apiKey?: string;
  /** True when a Gemini key is stored for this user (from pit.gemini_config_status()). */
  apiKeyConfigured?: boolean;
  apiKeyValidated?: boolean;
  apiKeyValidatedAt?: string;
  model?: string;
  
  // Safety Configuration
  harmCategory?: string;
  threshold?: string;
  
  // Generation Configuration
  temperature?: number;
  topP?: number;
  topK?: number;
  maxOutputTokens?: number;
  candidateCount?: number;
  
  // Context and Prompt Configuration
  systemInstruction?: string;
  contextWindow?: number;
  
  // Timing and Rate Limiting
  requestTimeout?: number;
  rateLimitRpm?: number;
  rateLimitTpm?: number;
  
  // Features
  enableCodeGeneration?: boolean;
  enableDataAnalysis?: boolean;
  enablePreferenceMatching?: boolean;
  enableScheduleOptimization?: boolean;
  enableReportGeneration?: boolean;
  
  // Notifications
  slackWebhookUrl?: string;
  emailNotifications?: boolean;
  adminEmail?: string;
  
  // Advanced
  debug?: boolean;
  logRequests?: boolean;
  cacheResponses?: boolean;
  enableFunctionCalling?: boolean;
}

/**
 * In-memory defaults for every Gemini setting.
 *
 * pit.gemini_config stores only api_key, model and enabled, so these are the
 * values for everything else — used both when no row exists and as the base a
 * loaded row is merged over. Previously they were inlined in one branch only,
 * which is how loading a row could blank them (issue #190).
 */
const DEFAULT_GEMINI_CONFIG: GeminiConfig = {
  model: 'gemini-pro',
  temperature: 0.4,
  topP: 0.95,
  topK: 64,
  maxOutputTokens: 8192,
  candidateCount: 1,
  contextWindow: 32768,
  requestTimeout: 30000,
  rateLimitRpm: 60,
  rateLimitTpm: 32000,
  enableCodeGeneration: true,
  enableDataAnalysis: true,
  enablePreferenceMatching: true,
  enableScheduleOptimization: true,
  enableReportGeneration: true,
  emailNotifications: false,
  debug: false,
  logRequests: true,
  cacheResponses: true,
  enableFunctionCalling: true
};

export interface AIRequest {
  id: string;
  timestamp: Date;
  user: string;
  feature: string;
  inputType: string;
  outputType: string;
  tokensUsed: number;
  costUsd: number;
  durationMs: number;
  success: boolean;
  error?: string;
}

interface GeminiContextType {
  config: GeminiConfig;
  loading: boolean;
  error: string | null;
  recentRequests: AIRequest[];
  updateConfig: (updates: Partial<GeminiConfig>) => Promise<void>;
  resetToDefaults: () => Promise<void>;
  validateApiKey: (apiKey: string) => Promise<boolean>;
  analyzeTeachingData: (data: any[]) => Promise<any>;
  generatePreferenceRecommendations: (staffId: string, data: any) => Promise<any>;
  optimizeSchedule: (grid: any, constraints: any) => Promise<any>;
  generateReports: (type: string, data: any) => Promise<any>;
  refreshRequests: () => Promise<void>;
}

const GeminiContext = createContext<GeminiContextType | undefined>(undefined);

export const useGemini = () => {
  const context = useContext(GeminiContext);
  if (context === undefined) {
    throw new Error('useGemini must be used within a GeminiProvider');
  }
  return context;
};

interface GeminiProviderProps {
  children: React.ReactNode;
}

export const GeminiProvider: React.FC<GeminiProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [config, setConfig] = useState<GeminiConfig>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recentRequests, setRecentRequests] = useState<AIRequest[]>([]);

  // Ref to track if operations should continue
  const isActiveRef = useRef(true);

  const loadConfig = useCallback(async () => {
    if (!user || !isActiveRef.current) return;

    setLoading(true);
    try {
      // Never select api_key: client roles have no column privilege on it
      // (issue #257). Whether a key is stored comes from the SECURITY DEFINER
      // status function, which returns a boolean, not the key.
      const { data, error } = await supabase
        .rpc('gemini_config_status')
        .maybeSingle<{ configured: boolean; model: string | null; enabled: boolean | null }>();

      // Check if still active before processing response
      if (!isActiveRef.current) return;

      if (error && error.code !== 'PGRST116' && !error.message?.includes('406')) { // PGRST116 = no rows returned, 406 = table doesn't exist
        throw error;
      }

      if (data) {
        // Read back only what the table stores, and MERGE over the defaults
        // rather than replacing them.
        //
        // This mapping used to read 29 columns, 25 of which do not exist. Every
        // one came back `undefined` and was written into config wholesale, so
        // loading a saved row silently blanked temperature, topP, the feature
        // flags and everything else — replacing working defaults with nothing.
        // Merging keeps the defaults for anything the table cannot hold.
        // See issue #190.
        const mappedConfig: GeminiConfig = {
          ...DEFAULT_GEMINI_CONFIG,
          apiKeyConfigured: !!data.configured,
          ...(data.model ? { model: data.model } : {})
        };
        setConfig(mappedConfig);
      } else {
        // No row yet — start from the shared defaults.
        setConfig(DEFAULT_GEMINI_CONFIG);
      }
    } catch (err) {
      console.error('Error loading Gemini config:', err);
      if (isActiveRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load configuration');
      }
    } finally {
      if (isActiveRef.current) {
        setLoading(false);
      }
    }
  }, [user]);

  const loadRecentRequests = useCallback(async () => {
    if (!user || !isActiveRef.current) return;

    try {
      const { data, error } = await supabase
        .from('ai_requests')
        .select('*')
        .eq('user_id', user.id)
        .order('timestamp', { ascending: false })
        .limit(50);

      // Check if still active before processing response
      if (!isActiveRef.current) return;

      if (error) throw error;

      const mappedRequests: AIRequest[] = (data || []).map(request => ({
        id: request.id,
        timestamp: new Date(request.timestamp),
        user: request.user_email,
        feature: request.feature,
        inputType: request.input_type,
        outputType: request.output_type,
        tokensUsed: request.tokens_used,
        costUsd: request.cost_usd,
        durationMs: request.duration_ms,
        success: request.success,
        error: request.error_message
      }));

      if (isActiveRef.current) {
        setRecentRequests(mappedRequests);
      }
    } catch (err) {
      console.error('Error loading AI requests:', err);
    }
  }, [user]);

  // Load config from Supabase on mount, reset on logout
  useEffect(() => {
    isActiveRef.current = true;

    if (user) {
      loadConfig();
      loadRecentRequests();
    } else {
      // Reset state when user logs out - batch all updates together
      setConfig({});
      setRecentRequests([]);
      setError(null);
      setLoading(false);
    }

    // Cleanup: mark as inactive when user changes or component unmounts
    return () => {
      isActiveRef.current = false;
    };
  }, [user, loadConfig, loadRecentRequests]);

  const updateConfig = async (updates: Partial<GeminiConfig>) => {
    if (!user) {
      setError('User not authenticated');
      return;
    }

    setLoading(true);
    try {
      // Only the columns pit.gemini_config actually has.
      //
      // This mapping used to list 29 fields — harm_category, temperature,
      // rate_limit_rpm and the rest. The table holds EIGHT columns:
      //
      //   id, user_id, api_key, model, enabled, created_at, updated_at, tenant_id
      //
      // Every absent column makes PostgREST reject the whole upsert with
      // PGRST204, so the write never landed. It went unnoticed because nothing
      // in the UI calls updateConfig and the table has zero rows (issue #190).
      //
      // The remaining settings are NOT dropped — they stay in React state via
      // the setConfig below, so the UI behaves the same within a session. They
      // simply do not survive a reload, which is the honest current state:
      // storage for them was never built.
      //
      // If you add a field here, add the column first. See also #155 — the
      // conflict target must name a unique index exactly.
      //
      // api_key is deliberately NOT written: client roles have no INSERT or
      // UPDATE privilege on it (issue #257), and nothing server-side consumes
      // it yet. Storing a key needs a server route (Vault) — see the issue.
      const dbUpdates: Record<string, unknown> = {
        user_id: user.id,
        model: updates.model,
        updated_at: new Date().toISOString()
      };

      // Remove undefined values
      const cleanedUpdates = Object.fromEntries(
        Object.entries(dbUpdates).filter(([_, value]) => value !== undefined)
      );

      const { error } = await supabase
        .from('gemini_config')
        .upsert(cleanedUpdates, { onConflict: GEMINI_CONFIG_CONFLICT_TARGET });

      if (error) throw error;

      setConfig(prev => ({ ...prev, ...updates }));
      setError(null);
    } catch (err) {
      console.error('Error updating Gemini config:', err);
      setError(err instanceof Error ? err.message : 'Failed to update configuration');
    } finally {
      setLoading(false);
    }
  };

  const resetToDefaults = async () => {
    // Third copy of these values, now the only one. The safety pair lives here
    // rather than in DEFAULT_GEMINI_CONFIG because it is a reset choice, not a
    // fallback for a missing row. See issue #190.
    const defaults: Partial<GeminiConfig> = {
      ...DEFAULT_GEMINI_CONFIG,
      harmCategory: 'HARM_CATEGORY_HARASSMENT',
      threshold: 'BLOCK_MEDIUM_AND_ABOVE'
    };

    await updateConfig(defaults);
  };

  const logAIRequest = async (request: Omit<AIRequest, 'id'>) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('ai_requests')
        .insert({
          user_id: user.id,
          user_email: request.user,
          timestamp: request.timestamp.toISOString(),
          feature: request.feature,
          input_type: request.inputType,
          output_type: request.outputType,
          tokens_used: request.tokensUsed,
          cost_usd: request.costUsd,
          duration_ms: request.durationMs,
          success: request.success,
          error_message: request.error
        });

      if (error) throw error;

      // Refresh recent requests
      await loadRecentRequests();
    } catch (err) {
      console.error('Error logging AI request:', err);
    }
  };

  const validateApiKey = async (apiKey: string): Promise<boolean> => {
    // TODO: Implement actual API key validation by making a test request to Gemini
    console.warn('GeminiContext: validateApiKey needs implementation');
    return false;
  };

  const analyzeTeachingData = async (data: any[]): Promise<any> => {
    // TODO: Implement AI analysis functionality
    console.warn('GeminiContext: analyzeTeachingData needs implementation');
    return { error: 'Feature not yet implemented' };
  };

  const generatePreferenceRecommendations = async (staffId: string, data: any): Promise<any> => {
    // TODO: Implement AI preference recommendations
    console.warn('GeminiContext: generatePreferenceRecommendations needs implementation');
    return { error: 'Feature not yet implemented' };
  };

  const optimizeSchedule = async (grid: any, constraints: any): Promise<any> => {
    // TODO: Implement AI schedule optimization
    console.warn('GeminiContext: optimizeSchedule needs implementation');
    return { error: 'Feature not yet implemented' };
  };

  const generateReports = async (type: string, data: any): Promise<any> => {
    // TODO: Implement AI report generation
    console.warn('GeminiContext: generateReports needs implementation');
    return { error: 'Feature not yet implemented' };
  };

  const refreshRequests = async () => {
    await loadRecentRequests();
  };

  const value: GeminiContextType = useMemo(() => ({
    config,
    loading,
    error,
    recentRequests,
    updateConfig,
    resetToDefaults,
    validateApiKey,
    analyzeTeachingData,
    generatePreferenceRecommendations,
    optimizeSchedule,
    generateReports,
    refreshRequests
  }), [config, loading, error, recentRequests]);

  return <GeminiContext.Provider value={value}>{children}</GeminiContext.Provider>;
};